import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import mermaid from 'mermaid';
import { DefaultTheme } from 'styled-components';
interface MermaidRendererProps {
  chart: string;
  className?: string;
}

function getMermaidThemeVariables(theme: DefaultTheme) {
  return {
    // Core theme settings
    darkMode: true,
    background: theme.colors.bgPage,
    fontFamily: 'Inter, sans-serif',
    // note - for some reason just setting the fontFamily will have the labels break on new lines. here we're decreasing the font size by 1 (deafult 16) to account for that
    fontSize: '15px',
    //   // Primary colors
    primaryColor: theme.colors.bgDefaultSecondary,
    primaryTextColor: theme.colors.textPrimary,
    primaryBorderColor: theme.colors.textRegular,

    // Secondary colors
    secondaryColor: theme.colors.bgDefault,
    secondaryTextColor: theme.colors.textRegular,
    secondaryBorderColor: theme.colors.textDisabled,

    // Tertiary colors
    tertiaryColor: theme.colors.bgActive,
    tertiaryTextColor: theme.colors.textActive,
    tertiaryBorderColor: theme.colors.textRegular,

    // Text and line colors
    textColor: theme.colors.textPrimary,
    lineColor: theme.colors.textRegular,

    // Main background
    mainBkg: theme.colors.bgDefaultSecondary,

    // Note styling
    noteBkgColor: theme.colors.bgDefault,
    noteTextColor: theme.colors.textPrimary,
    noteBorderColor: theme.colors.textRegular,

    // Error styling
    errorBkgColor: theme.colors.bgDanger,
    errorTextColor: theme.colors.textDanger,

    // Flowchart specific
    nodeBorder: theme.colors.textRegular,
    clusterBkg: theme.colors.bgPage2,
    clusterBorder: theme.colors.textRegular,
    defaultLinkColor: theme.colors.textRegular,
    titleColor: theme.colors.textRegular,
    // edgeLabelBackground: theme.colors.bgDefaultSecondary,
    edgeLabelBackground: '#8A8A8B',
    nodeTextColor: theme.colors.textPrimary,

    // Sequence diagram specific
    actorBkg: theme.colors.bgDefaultSecondary,
    actorBorder: theme.colors.textRegular,
    actorTextColor: theme.colors.textPrimary,
    actorLineColor: theme.colors.textRegular,
    signalColor: theme.colors.textRegular,
    signalTextColor: theme.colors.textPrimary,
    labelBoxBkgColor: theme.colors.bgDefaultSecondary,
    labelBoxBorderColor: theme.colors.textRegular,
    labelTextColor: theme.colors.textPrimary,
    loopTextColor: theme.colors.textPrimary,
    activationBorderColor: theme.colors.textRegular,
    activationBkgColor: theme.colors.bgDefault,
    sequenceNumberColor: theme.colors.textRegular,

    // State diagram specific
    labelColor: theme.colors.textPrimary,
    altBackground: theme.colors.bgActive,

    // Class diagram specific
    classText: theme.colors.textPrimary,

    // User journey specific
    fillType0: theme.colors.primary,
    fillType1: theme.colors.secondary,
    fillType2: theme.colors.highlight,
    fillType3: theme.colors.buttonTertiary,
    fillType4: theme.colors.primary,
    fillType5: theme.colors.secondary,
    fillType6: theme.colors.highlight,
    fillType7: theme.colors.buttonTertiary,

    // Pie chart specific
    pie1: theme.colors.primary,
    pie2: theme.colors.secondary,
    pie3: theme.colors.highlight,
    pie4: theme.colors.buttonTertiary,
    pie5: theme.colors.primary,
    pie6: theme.colors.secondary,
    pie7: theme.colors.highlight,
    pie8: theme.colors.buttonTertiary,
    pie9: theme.colors.primary,
    pie10: theme.colors.secondary,
    pie11: theme.colors.highlight,
    pie12: theme.colors.buttonTertiary,
    pieTitleTextSize: '16px',
    pieTitleTextColor: theme.colors.textPrimary,
    pieSectionTextSize: '14px',
    pieSectionTextColor: theme.colors.textPrimary,
    pieLegendTextSize: '14px',
    pieLegendTextColor: theme.colors.textPrimary,
    pieStrokeColor: theme.colors.textRegular,
    pieStrokeWidth: '2px',
    pieOuterStrokeWidth: '2px',
    pieOuterStrokeColor: theme.colors.textRegular,
    pieOpacity: 0.8,
  };
}

const MermaidRenderer: React.FC<MermaidRendererProps> = ({
  chart,
  className,
}) => {
  const theme = useTheme();
  const elementRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const renderChart = async () => {
      if (!chart || !elementRef.current) return;

      try {
        setError('');

        // Initialize Mermaid with theme-based configuration
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          darkMode: true,
          themeVariables: getMermaidThemeVariables(theme),
          securityLevel: 'loose', // Allow HTML in diagrams
          htmlLabels: false,
        });

        // Generate a unique ID for this chart
        const id = `mermaid-${Math.random().toString(36).slice(2)}`;

        // Render the chart and get the SVG
        const { svg: renderedSvg } = await mermaid.render(id, chart);
        setSvg(renderedSvg);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(
          `Error rendering diagram: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`
        );
      }
    };

    renderChart();
  }, [chart, theme]);

  if (error) {
    return (
      <div
        className={`mermaid-error ${className || ''}`}
        style={{
          background: theme.colors.bgDanger,
          border: `1px solid ${theme.colors.textDanger}`,
          borderRadius: theme.dimensions.sectionBorderRadius,
          padding: '12px',
          color: theme.colors.textDanger,
          fontFamily: theme.fonts.playground,
        }}
      >
        <strong>Mermaid Error:</strong> {error}
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer' }}>Chart Code</summary>
          <pre
            style={{
              background: theme.colors.fieldBackground,
              padding: '8px',
              borderRadius: '4px',
              marginTop: '8px',
              overflow: 'auto',
              color: theme.colors.textPrimary,
            }}
          >
            {chart}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <MermaidContainer
      ref={elementRef}
      className={`mermaid-container ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidRenderer;

const MermaidContainer = styled.div`
  /* Override specific global styles that might interfere */
  p {
    color: revert !important;
    font-size: revert !important;
    margin: 0;
    padding: 0;
  }
  text-align: center;

  margin: 16px 0 !important;
  .mermaid svg {
    text-align: center;
  }
`;
